import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BarChart3, MessageSquare, Calculator, LogOut, Send, Trash2, TrendingUp, Users, Target, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========== STATS CARDS =========== */
function StatsCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</p>
      <p className="text-xs text-slate-500 mt-1 font-medium">{label}</p>
    </div>
  );
}

/* =========== DASHBOARD TAB =========== */
function DashboardTab({ stats, loading }) {
  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#2E7CF6] animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-tab">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Performance</h2>
        <p className="text-sm text-slate-500">Ihre Lead-Pipeline auf einen Blick</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard label="Leads Gesamt" value={stats.total_leads} icon={Users} color="bg-blue-50 text-[#2E7CF6]" />
        <StatsCard label="Offen" value={stats.leads_offen} icon={Clock} color="bg-slate-100 text-slate-600" />
        <StatsCard label="Meeting gebucht" value={stats.leads_meeting} icon={Target} color="bg-sky-50 text-sky-600" />
        <StatsCard label="Kunde gewonnen" value={stats.leads_kunde} icon={CheckCircle} color="bg-emerald-50 text-emerald-600" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard label="Kein Fit" value={stats.leads_kein_fit} icon={XCircle} color="bg-red-50 text-red-500" />
        <StatsCard label="Nicht erreicht" value={stats.leads_nicht_erreicht} icon={Clock} color="bg-amber-50 text-amber-600" />
        <StatsCard label="Conversion Rate" value={`${stats.conversion_rate}%`} icon={TrendingUp} color="bg-violet-50 text-violet-600" />
        <StatsCard label="Termine (valid)" value={stats.valid_appointments} icon={CheckCircle} color="bg-emerald-50 text-emerald-600" />
      </div>

      {/* Pipeline visual */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Lead-Pipeline</h3>
        <div className="space-y-3">
          {[
            { label: 'Offen', value: stats.leads_offen, color: 'bg-slate-400', total: stats.total_leads },
            { label: 'Meeting', value: stats.leads_meeting, color: 'bg-sky-500', total: stats.total_leads },
            { label: 'Kunde', value: stats.leads_kunde, color: 'bg-emerald-500', total: stats.total_leads },
            { label: 'Kein Fit', value: stats.leads_kein_fit, color: 'bg-red-400', total: stats.total_leads },
            { label: 'Nicht erreicht', value: stats.leads_nicht_erreicht, color: 'bg-amber-400', total: stats.total_leads },
          ].map((item) => {
            const pct = item.total > 0 ? (item.value / item.total * 100) : 0;
            return (
              <div key={item.label} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-28 shrink-0">{item.label}</span>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${item.color} rounded-full transition-all duration-700`} style={{ width: `${Math.max(pct, 0)}%` }} />
                </div>
                <span className="text-xs font-bold text-slate-700 w-8 text-right">{item.value}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* =========== AI BOT TAB =========== */
function BotTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text, created_at: new Date().toISOString() }]);
    setSending(true);

    try {
      const res = await fetch(`${API_URL}/api/bot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response, created_at: new Date().toISOString() }]);
      } else {
        toast.error('Fehler beim Senden');
      }
    } catch {
      toast.error('Netzwerkfehler');
    }
    setSending(false);
  };

  const clearChat = async () => {
    if (!window.confirm('Chat-Verlauf wirklich löschen?')) return;
    await fetch(`${API_URL}/api/bot/history`, { method: 'DELETE', credentials: 'include' });
    setMessages([]);
    toast.success('Chat gelöscht');
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)]" data-testid="bot-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Business-Berater</h2>
          <p className="text-sm text-slate-500">Ihr persönlicher AI-Berater für Vertrieb, Marketing & Skalierung</p>
        </div>
        {messages.length > 0 && (
          <button onClick={clearChat} className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors" data-testid="clear-chat">
            <Trash2 className="w-3.5 h-3.5" /> Löschen
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-white border border-slate-200 rounded-xl p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-[#2E7CF6] animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 text-sm mb-6">Stellen Sie eine Frage zu Vertrieb, Marketing oder Unternehmensstrategie.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['Wie optimiere ich meinen Sales-Funnel?', 'Preiserhöhung kommunizieren', 'Kaltakquise Skript erstellen'].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 hover:border-[#2E7CF6]/30 hover:text-[#2E7CF6] transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white'
                  : 'bg-slate-50 border border-slate-200 text-slate-800'
              }`}>
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>
            </div>
          ))
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Denkt nach...
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="mt-4 flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Ihre Frage..."
          className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#2E7CF6]/40"
          data-testid="bot-input"
          disabled={sending}
        />
        <button
          onClick={handleSend}
          disabled={sending || !input.trim()}
          className="w-11 h-11 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white rounded-xl flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-all shrink-0"
          data-testid="bot-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* =========== ROI RECHNER TAB =========== */
function RoiTab() {
  const [avgDeal, setAvgDeal] = useState(5000);
  const [closeRate, setCloseRate] = useState(20);
  const [termineMonat, setTermineMonat] = useState(10);
  const [kostenTermin, setKostenTermin] = useState(200);

  const abschluesse = Math.round(termineMonat * (closeRate / 100));
  const umsatz = abschluesse * avgDeal;
  const investition = termineMonat * kostenTermin;
  const gewinn = umsatz - investition;
  const roi = investition > 0 ? Math.round((gewinn / investition) * 100) : 0;
  const maxProTermin = closeRate > 0 ? Math.round(avgDeal * (closeRate / 100)) : 0;

  return (
    <div className="space-y-8" data-testid="roi-tab">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Performance-Rechner</h2>
        <p className="text-sm text-slate-500">Berechnen Sie Ihren ROI pro Termin und Monat</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Ihre Zahlen</h3>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Durchschnittlicher Auftragswert (EUR)</label>
            <input
              type="number"
              value={avgDeal}
              onChange={(e) => setAvgDeal(Number(e.target.value))}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-slate-900 text-lg font-bold focus:outline-none focus:border-[#2E7CF6]/40"
              data-testid="roi-avg-deal"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Abschlussquote</label>
              <span className="text-sm font-bold text-[#2E7CF6]">{closeRate}%</span>
            </div>
            <input
              type="range"
              min="5"
              max="80"
              value={closeRate}
              onChange={(e) => setCloseRate(Number(e.target.value))}
              className="w-full accent-[#2E7CF6]"
              data-testid="roi-close-rate"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1"><span>5%</span><span>80%</span></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Termine pro Monat</label>
              <span className="text-sm font-bold text-[#2E7CF6]">{termineMonat}</span>
            </div>
            <input
              type="range"
              min="1"
              max="50"
              value={termineMonat}
              onChange={(e) => setTermineMonat(Number(e.target.value))}
              className="w-full accent-[#2E7CF6]"
              data-testid="roi-termine"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1"><span>1</span><span>50</span></div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Kosten pro Termin (EUR)</label>
            <input
              type="number"
              value={kostenTermin}
              onChange={(e) => setKostenTermin(Number(e.target.value))}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-slate-900 text-lg font-bold focus:outline-none focus:border-[#2E7CF6]/40"
              data-testid="roi-kosten"
            />
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#2E7CF6] to-[#1B5FD4] rounded-xl p-6 text-white">
            <p className="text-sm text-white/70 mb-1">Max. Budget pro Termin</p>
            <p className="text-4xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{maxProTermin.toLocaleString('de-DE')} EUR</p>
            <p className="text-xs text-white/50 mt-2">Bei {closeRate}% Abschlussquote und {avgDeal.toLocaleString('de-DE')} EUR Auftragswert</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Monatliche Prognose</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <span className="text-sm text-slate-600">Termine</span>
                <span className="text-sm font-bold text-slate-900">{termineMonat}</span>
              </div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <span className="text-sm text-slate-600">Erwartete Abschlüsse</span>
                <span className="text-sm font-bold text-slate-900">{abschluesse}</span>
              </div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <span className="text-sm text-slate-600">Erwarteter Umsatz</span>
                <span className="text-sm font-bold text-emerald-600">{umsatz.toLocaleString('de-DE')} EUR</span>
              </div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <span className="text-sm text-slate-600">Investition</span>
                <span className="text-sm font-bold text-slate-900">{investition.toLocaleString('de-DE')} EUR</span>
              </div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <span className="text-sm text-slate-600">Netto-Gewinn</span>
                <span className={`text-sm font-bold ${gewinn >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{gewinn.toLocaleString('de-DE')} EUR</span>
              </div>
              <div className="flex justify-between items-center pt-1">
                <span className="text-sm font-bold text-slate-700">ROI</span>
                <span className={`text-lg font-black ${roi >= 0 ? 'text-emerald-600' : 'text-red-500'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{roi}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========== MAIN SaaS PAGE =========== */
export default function SaasPage() {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authUser) {
      navigate('/portal');
      return;
    }
    fetchStats();
  }, [authUser, navigate]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/client/stats`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
    setLoading(false);
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'bot', label: 'Business-Berater', icon: MessageSquare },
    { id: 'roi', label: 'ROI-Rechner', icon: Calculator },
  ];

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <span className="font-black text-xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>VALORYX</span>
          <div className="flex items-center gap-4">
            {authUser && (
              <span className="text-sm text-slate-500 hidden md:block">{authUser.name || authUser.email}</span>
            )}
            <button
              onClick={() => { logout(); navigate('/portal'); }}
              className="text-sm text-slate-400 hover:text-slate-700 flex items-center gap-1 transition-colors"
              data-testid="saas-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Tab nav */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-8 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`saas-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'dashboard' && <DashboardTab stats={stats || {}} loading={loading} />}
        {activeTab === 'bot' && <BotTab />}
        {activeTab === 'roi' && <RoiTab />}
      </div>
    </div>
  );
}
